"use client";

import { useContext, useState, useEffect } from "react";
import { SectionContext } from "@contexts";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";

import { Box } from "@mui/material";

const Collapse = dynamic(() => import("@mui/material/Collapse"), {
  ssr: false,
});
const Tabs = dynamic(() => import("@mui/material/Tabs"), {
  ssr: false,
});
const Tooltip = dynamic(() => import("@mui/material/Tooltip"), {
  ssr: false,
});

import {
  HeaderContainer,
  HeaderAvatarBox,
  HeaderAvatarImg,
  HeaderTitleTextWrapper,
  HeaderTitleText,
  Tab,
} from "./Header.styles";

const MobileMenu = dynamic(() => import("@components/elements/MobileMenu"));

function LinkTab({ title, icon, ...rest }) {
  return (
    <Box>
      <Tooltip title={title} enterTouchDelay={0} {...rest}>
        <Tab component="div" icon={icon} tabIndex={0} />
      </Tooltip>
    </Box>
  );
}
export default function Header({ headerAvatar, navItems }) {
  const totalSections = navItems?.length;
  const [headerAvatarHover, setHeaderAvatarHover] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  const { parallaxScrollerRef } = useContext(SectionContext) || {};

  useEffect(() => {
    const callback = () => {
      const scrollHeight =
        parallaxScrollerRef.current.container.current.scrollHeight;
      const pageHeight = scrollHeight / totalSections;
      const currentPage = Math.round(
        parallaxScrollerRef.current.current / pageHeight
      );
      setCurrentSection(currentPage);
    };

    document.addEventListener("scroll", callback, true);

    return () => {
      document.removeEventListener("scroll", callback);
    };
  }, []);

  const handleSelectedTabChange = (event, newValue) => {
    event?.preventDefault();
    if (!parallaxScrollerRef.current.isScrolling) {
      parallaxScrollerRef.current.scrollTo(newValue);
      parallaxScrollerRef.current.isScrolling = true;
      setTimeout(() => {
        parallaxScrollerRef.current.isScrolling = false;
      }, 500);
    }
  };

  return (
    <HeaderContainer>
      <HeaderAvatarBox
        onMouseEnter={() => setHeaderAvatarHover(true)}
        onMouseLeave={() => setHeaderAvatarHover(false)}
        onFocus={() => setHeaderAvatarHover(true)}
        onBlur={() => setHeaderAvatarHover(false)}
        tabIndex={0}
      >
        <HeaderAvatarImg
          src={headerAvatar?.src}
          alt={headerAvatar?.alt || "Jace Edwards coding portfolio avatar"}
        />
        <Collapse in={headerAvatarHover} orientation="horizontal" timeout={300}>
          <HeaderTitleTextWrapper>
            <HeaderTitleText variant="h1">
              Jace Edwards{"\n"}
              Full Stack Developer
            </HeaderTitleText>
          </HeaderTitleTextWrapper>
        </Collapse>
      </HeaderAvatarBox>
      {/* Desktop Navigation */}
      <Tabs
        value={currentSection}
        onChange={handleSelectedTabChange}
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiTabs-indicator": { backgroundColor: "white" },
        }}
      >
        {Array.isArray(navItems) ? (
          navItems.map((navItem) => (
            <LinkTab key={navItem?.title} {...navItem} />
          ))
        ) : (
          <Box />
        )}
      </Tabs>
      {/* Mobile Navigation */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <MobileMenu
          navItems={navItems}
          handleSelectedTabChange={handleSelectedTabChange}
        />
      </Box>
    </HeaderContainer>
  );
}

LinkTab.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.node,
};

Header.propTypes = {
  navItems: PropTypes.instanceOf(Array),
  headerAvatar: PropTypes.instanceOf(Object),
};
